import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useTelegram, TelegramUser } from '../hooks/useTelegram';
import { initializeApiAuth } from '../services/api';

interface AuthContextType {
  user: TelegramUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isTelegramWebApp: boolean;
  authData: string;
  logout: () => void;
  checkGroupMembership: () => Promise<boolean>;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const telegram = useTelegram();
  const [isLoading, setIsLoading] = useState(true);
  const [hasValidGroupMembership, setHasValidGroupMembership] = useState(false);

  const checkGroupMembership = useCallback(async (): Promise<boolean> => {
    // For now, we'll return true if we're in a valid Telegram environment
    if (!telegram.isAuthenticated) {
      console.log('❌ Not authenticated via Telegram');
      return false;
    }

    // Special case for Telegram Desktop where user data might not be available
    if (telegram.isTelegramWebApp && !telegram.user) {
      console.log('⚠️ Telegram WebApp detected but no user data (probably Desktop)');

      // Allow access if we're in development or have valid WebApp environment
      if (process.env.NODE_ENV === 'development' || telegram.tg?.platform) {
        console.log('✅ Allowing access due to valid Telegram WebApp environment');
        return true;
      }
    }

    try {
      // TODO: Replace with actual API call to your bot/backend
      // const response = await fetch('/api/validate-user', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     userId: telegram.user?.id || 'unknown',
      //     platform: telegram.tg?.platform,
      //     authData: telegram.authData
      //   })
      // });
      // return response.ok;

      // For development, return true if we have a valid Telegram environment
      const userId = telegram.user?.id || 'Desktop-User';
      console.log('✅ Checking group membership for user:', userId, 'on platform:', telegram.tg?.platform);
      return true;
    } catch (error) {
      console.error('Failed to check group membership:', error);
      return false;
    }
  }, [telegram.isAuthenticated, telegram.user, telegram.isTelegramWebApp, telegram.tg]);

  const hasPermission = (permission: string): boolean => {
    // Basic permission system - can be extended
    if (!telegram.isAuthenticated || !hasValidGroupMembership) {
      return false;
    }

    // For now, all authenticated group members have all permissions
    // This can be extended to role-based permissions
    return true;
  };

  const logout = () => {
    setHasValidGroupMembership(false);
    // Clear any local storage if needed
    localStorage.removeItem('auth_token');

    // Close Telegram WebApp
    if (telegram.tg) {
      telegram.tg.close();
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      console.log('🚀 Initializing auth context...', {
        isAuthenticated: telegram.isAuthenticated,
        isTelegramWebApp: telegram.isTelegramWebApp,
        hasUser: !!telegram.user
      });

      setIsLoading(true);

      // Initialize API authentication with Telegram data
      initializeApiAuth(
        () => telegram.authData,
        () => telegram.user
      );

      // Always set loading to false after a maximum delay
      const maxLoadingTime = setTimeout(() => {
        console.log('⏰ Max loading time reached, stopping loader');
        setIsLoading(false);
        if (!telegram.isAuthenticated) {
          setHasValidGroupMembership(false);
        }
      }, 3000);

      try {
        // Wait for Telegram initialization
        if (telegram.isTelegramWebApp && telegram.isAuthenticated) {
          console.log('✅ Telegram WebApp authenticated, checking group membership');
          const hasAccess = await checkGroupMembership();
          setHasValidGroupMembership(hasAccess);

          if (!hasAccess) {
            telegram.showAlert('Access denied. You must be a member of the authorized group to use this application.');
          }
        } else if (process.env.NODE_ENV === 'development' && telegram.isAuthenticated) {
          // Development mode fallback
          console.warn('🔧 Development mode: Skipping group membership check');
          setHasValidGroupMembership(true);
        } else if (!telegram.isAuthenticated) {
          console.log('❌ Not authenticated via Telegram');
          setHasValidGroupMembership(false);
        }
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
        setHasValidGroupMembership(false);
      } finally {
        clearTimeout(maxLoadingTime);
        setIsLoading(false);
      }
    };

    // Initialize after a short delay to ensure Telegram is ready
    const timer = setTimeout(initializeAuth, 500);
    return () => clearTimeout(timer);
  }, [telegram.isAuthenticated, telegram.isTelegramWebApp, telegram.authData, telegram.user, checkGroupMembership, telegram]);

  const contextValue: AuthContextType = {
    user: telegram.user,
    isAuthenticated: telegram.isAuthenticated && hasValidGroupMembership,
    isLoading,
    isTelegramWebApp: telegram.isTelegramWebApp,
    authData: telegram.authData,
    logout,
    checkGroupMembership,
    hasPermission
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};